import {
  base64ToBytes,
  bytesToBase64,
  MAX_DICTATION_BYTES,
  MAX_DICTATION_SECONDS,
  MAX_SPEECH_AUDIO_BYTES,
  speechTextReason,
} from "@/lib/assistant/speech-contract";
import type { VoiceTurn } from "@/lib/voice/conversation";
export type SpeechTurn = VoiceTurn & { messageEnvelope?: string; conversationId?: string };
export interface SpeechState {
  dictation: "idle" | "starting" | "recording" | "confirm" | "transcribing" | "error";
  seconds: number;
  dictationError?: string;
  readAloud: "idle" | "loading" | "playing" | "completed" | "blocked" | "error";
  messageId?: string;
  readError?: string;
}
export const IDLE_SPEECH: SpeechState = { dictation: "idle", seconds: 0, readAloud: "idle" };
export const dictationBusy = (state: SpeechState) =>
  ["starting", "recording", "confirm", "transcribing"].includes(state.dictation);
export interface SpeechPlayer {
  play: () => Promise<void>;
  stop: () => void;
}
export interface SpeechCallbacks {
  canDictate: () => boolean;
  canCaptureApproval?: () => boolean;
  canRead: (turn: SpeechTurn) => boolean;
  appendDraft: (text: string) => void;
  changed: (state: SpeechState) => void;
}
export interface SpeechDependencies {
  actorId: string;
  invoke: (body: Record<string, unknown>, signal: AbortSignal) => Promise<unknown>;
  capture?: () => Promise<MediaStream>;
  recorder?: (stream: MediaStream, mime: string) => MediaRecorder;
  supportedMime?: () => string | null;
  player?: (audio: Uint8Array, ended: () => void, failed: () => void) => SpeechPlayer;
}
type CommittedSpeechDependencies = SpeechDependencies & SpeechCallbacks;

export interface ApprovalCaptureRequest {
  capture?: () => Promise<MediaStream>;
  challenge: string;
  conversationId: string;
  proposalId: string;
  revision: number;
}

export interface ApprovalCaptureResult {
  transcript: string;
}

interface ApprovalCapture {
  reject: (reason: Error) => void;
  resolve: (result: ApprovalCaptureResult) => void;
  request: ApprovalCaptureRequest;
}
function haltRecorder(recording: MediaRecorder) {
  if (recording.state !== "inactive") recording.stop();
}
function isValidApprovalTranscript(value: unknown): value is string {
  return typeof value === "string" && Boolean(value.trim()) && value.length <= 16_000;
}
function isTranscriptionSuperseded(
  signal: AbortSignal,
  generation: number,
  currentGeneration: number,
): boolean {
  return signal.aborted || generation !== currentGeneration;
}
function isTranscriptionPermitted(
  capture: ApprovalCapture | null,
  deps: CommittedSpeechDependencies,
): boolean {
  return capture ? deps.canCaptureApproval?.() === true : deps.canDictate();
}
function isValidDictationText(result: { text?: unknown }): result is { text: string } {
  return typeof result.text === "string" && result.text.length <= 16_000;
}
function canCapture(approval: boolean, deps: CommittedSpeechDependencies): boolean {
  return Boolean(approval ? deps.canCaptureApproval?.() : deps.canDictate());
}
function canStartDictation(
  approval: boolean,
  deps: CommittedSpeechDependencies,
  state: SpeechState,
): boolean {
  return (
    canCapture(approval, deps) &&
    !dictationBusy(state) &&
    !["loading", "playing"].includes(state.readAloud)
  );
}
function browserMime() {
  if (typeof MediaRecorder === "undefined") return null;
  return (
    [
      "audio/webm;codecs=opus",
      "audio/ogg;codecs=opus",
      "audio/mp4;codecs=mp4a.40.2",
      "audio/mp4",
    ].find((mime) => MediaRecorder.isTypeSupported(mime)) ?? null
  );
}
function browserPlayer(bytes: Uint8Array, ended: () => void, failed: () => void): SpeechPlayer {
  const url = URL.createObjectURL(new Blob([bytes as BlobPart], { type: "audio/mpeg" }));
  const audio = new Audio(url);
  let stopped = false;
  const stop = () => {
    if (stopped) return;
    stopped = true;
    audio.pause();
    audio.onended = null;
    audio.onerror = null;
    audio.removeAttribute("src");
    URL.revokeObjectURL(url);
  };
  audio.onended = () => {
    stop();
    ended();
  };
  audio.onerror = () => {
    stop();
    failed();
  };
  return { play: () => audio.play(), stop };
}
interface Authorization {
  conversationId: string;
  textHash: string;
  expiresAt: number;
  voice: string;
  model: string;
}
interface SpeechCache {
  key: string;
  source: string;
  bytes: Uint8Array;
  expiresAt: number;
}
/**
 * A blocked replay is the same read that just failed to start: the cached bytes
 * and the unexpired server authorization for this exact turn are already in
 * hand, so Play can call `play()` without an awaited network call. That keeps
 * the click gesture alive on Safari, which otherwise drops it across the
 * authorize round trip and refuses playback again.
 */
function isBlockedReplay(
  state: SpeechState,
  turn: SpeechTurn,
  source: string,
  cache: SpeechCache | null,
): cache is SpeechCache {
  return (
    state.readAloud === "blocked" &&
    state.messageId === turn.id &&
    cache !== null &&
    cache.source === source &&
    cache.expiresAt > Date.now()
  );
}
function authorization(value: unknown): Authorization {
  const result = value as Authorization | null;
  if (
    !result ||
    ![result.conversationId, result.textHash, result.voice, result.model].every(
      (field) => typeof field === "string" && field.length > 0,
    ) ||
    !Number.isFinite(result.expiresAt) ||
    result.expiresAt <= Date.now()
  )
    throw new Error("Read aloud authorization expired.");
  return result;
}

/** One owner for capture, finalization, generation and playback. No media is created on construction. */
export function createStandaloneSpeech(dependencies: SpeechDependencies) {
  // Construction acquires no media and denies actions until its owner commits policy.
  const deps: CommittedSpeechDependencies = {
    ...dependencies,
    canDictate: () => false,
    canCaptureApproval: () => false,
    canRead: () => false,
    appendDraft: () => undefined,
    changed: () => undefined,
  };
  let state: SpeechState = { ...IDLE_SPEECH };
  const update = (patch: Partial<SpeechState>) => {
    if (
      Object.entries(patch).every(([key, value]) =>
        Object.is(state[key as keyof SpeechState], value),
      )
    )
      return;
    state = { ...state, ...patch };
    deps.changed(state);
  };
  let recordGeneration = 0,
    readGeneration = 0;
  let recorder: MediaRecorder | null = null,
    microphone: MediaStream | null = null;
  let chunks: Blob[] = [],
    recording: Blob | null = null,
    size = 0,
    tooLarge = false,
    transcribeOnStop = false;
  let approvalCapture: ApprovalCapture | null = null;
  let clock: ReturnType<typeof setInterval> | undefined,
    deadline: ReturnType<typeof setTimeout> | undefined;
  let dictationAbort: AbortController | null = null,
    readAbort: AbortController | null = null;
  let player: SpeechPlayer | null = null;
  let pendingRead: Promise<void> | null = null;
  let readSource: string | null = null;
  let cache: SpeechCache | null = null;
  const releaseMicrophone = () => {
    clearInterval(clock);
    clearTimeout(deadline);
    microphone?.getTracks().forEach((track) => track.stop());
    microphone = null;
  };
  const cancelDictation = () => {
    recordGeneration++;
    dictationAbort?.abort();
    dictationAbort = null;
    if (recorder) {
      recorder.ondataavailable = null;
      recorder.onstop = null;
      recorder.onerror = null;
      haltRecorder(recorder);
      recorder = null;
    }
    releaseMicrophone();
    chunks = [];
    recording = null;
    size = 0;
    const capture = approvalCapture;
    approvalCapture = null;
    capture?.reject(new Error("Voice confirmation was canceled."));
    update({ dictation: "idle", seconds: 0, dictationError: undefined });
  };
  const transcribe = async (generation: number) => {
    const capture = approvalCapture;
    if (!recording || generation !== recordGeneration || !isTranscriptionPermitted(capture, deps)) {
      cancelDictation();
      return;
    }
    const blob = recording;
    recording = null;
    dictationAbort = new AbortController();
    const signal = dictationAbort.signal;
    update({ dictation: "transcribing", dictationError: undefined });
    try {
      const audio = bytesToBase64(new Uint8Array(await blob.arrayBuffer()));
      if (isTranscriptionSuperseded(signal, generation, recordGeneration)) return;
      const result = (await deps.invoke(
        capture
          ? {
              action: "approvalCapture",
              audio,
              challenge: capture.request.challenge,
              conversationId: capture.request.conversationId,
              mime: blob.type,
              proposalId: capture.request.proposalId,
              requestId: crypto.randomUUID(),
              revision: capture.request.revision,
            }
          : { action: "dictation", audio, mime: blob.type, requestId: crypto.randomUUID() },
        signal,
      )) as { text?: unknown; transcript?: unknown };
      if (
        isTranscriptionSuperseded(signal, generation, recordGeneration) ||
        !isTranscriptionPermitted(capture, deps)
      )
        return;
      if (capture) {
        if (!isValidApprovalTranscript(result.transcript))
          throw new Error("Invalid approval capture");
        dictationAbort = null;
        recordGeneration++;
        approvalCapture = null;
        capture.resolve({ transcript: result.transcript });
        update({ dictation: "idle", dictationError: undefined, seconds: 0 });
        return;
      }
      if (!isValidDictationText(result)) throw new Error("Invalid transcription");
      const text = result.text.trim();
      dictationAbort = null;
      recordGeneration++; // A completion can append at most once, including duplicate/late events.
      if (text) deps.appendDraft(text);
      update({
        dictation: "idle",
        seconds: 0,
        dictationError: text ? undefined : "No speech was recognized. Your draft is unchanged.",
      });
    } catch {
      const failedCapture = approvalCapture;
      approvalCapture = null;
      failedCapture?.reject(new Error("Voice confirmation could not be verified."));
      if (!signal.aborted && generation === recordGeneration)
        update({
          dictation: "error",
          dictationError: "Dictation failed. Your draft is unchanged. Record again to retry.",
        });
    } finally {
      if (generation === recordGeneration) dictationAbort = null;
    }
  };
  const stopCapture = (confirm: boolean) => {
    if (!recorder || recorder.state === "inactive") return;
    transcribeOnStop = confirm;
    releaseMicrophone();
    haltRecorder(recorder);
  };
  const startDictation = async (
    approval = false,
    captureOverride?: () => Promise<MediaStream>,
  ): Promise<boolean> => {
    if (!canStartDictation(approval, deps, state)) return false;
    const mime = (deps.supportedMime ?? browserMime)();
    if (!mime) {
      update({
        dictation: "error",
        dictationError: "This browser cannot record supported dictation audio.",
      });
      return false;
    }
    const generation = ++recordGeneration;
    chunks = [];
    size = 0;
    tooLarge = false;
    recording = null;
    transcribeOnStop = false;
    update({ dictation: "starting", seconds: 0, dictationError: undefined });
    try {
      const stream = await (
        captureOverride ??
        deps.capture ??
        (() => navigator.mediaDevices.getUserMedia({ audio: true }))
      )();
      if (generation !== recordGeneration || !canCapture(approval, deps)) {
        stream.getTracks().forEach((track) => track.stop());
        return false;
      }
      microphone = stream;
      recorder = (
        deps.recorder ??
        ((source, type) =>
          new MediaRecorder(source, { mimeType: type, audioBitsPerSecond: 64_000 }))
      )(stream, mime);
      const owned = recorder;
      owned.ondataavailable = (event) => {
        if (generation !== recordGeneration || !event.data.size) return;
        size += event.data.size;
        if (size <= MAX_DICTATION_BYTES) chunks.push(event.data);
        else tooLarge = true;
        if (size >= MAX_DICTATION_BYTES) stopCapture(false);
      };
      owned.onerror = () => {
        if (generation !== recordGeneration) return;
        cancelDictation();
        update({
          dictation: "error",
          dictationError: "Recording failed. Your draft is unchanged.",
        });
      };
      owned.onstop = () => {
        if (generation !== recordGeneration) return;
        releaseMicrophone();
        recorder = null;
        if (tooLarge || !size) {
          chunks = [];
          update({
            dictation: "error",
            dictationError: "Recording is empty or exceeds 5 MiB. Your draft is unchanged.",
          });
          return;
        }
        recording = new Blob(chunks, { type: owned.mimeType || mime });
        chunks = [];
        if (transcribeOnStop) void transcribe(generation);
        else
          update({
            dictation: "confirm",
            dictationError: "Recording limit reached. Transcribe or discard this recording.",
          });
      };
      owned.start(250);
      const started = Date.now();
      update({ dictation: "recording" });
      clock = setInterval(
        () =>
          update({
            seconds: Math.min(MAX_DICTATION_SECONDS, Math.floor((Date.now() - started) / 1000)),
          }),
        250,
      );
      // Leave room for the encoder's final packet while keeping the recording below 60 seconds.
      deadline = setTimeout(() => stopCapture(false), MAX_DICTATION_SECONDS * 1000 - 250);
      return true;
    } catch {
      if (generation === recordGeneration) {
        cancelDictation();
        update({
          dictation: "error",
          dictationError: "Microphone access or recording is unavailable. Your draft is unchanged.",
        });
      }
      return false;
    }
  };
  const startApprovalCapture = async (
    request: ApprovalCaptureRequest,
  ): Promise<ApprovalCaptureResult> =>
    await new Promise<ApprovalCaptureResult>((resolve, reject) => {
      if (!deps.canCaptureApproval?.() || dictationBusy(state)) {
        reject(new Error("Voice confirmation is unavailable."));
        return;
      }
      approvalCapture = { reject, request, resolve };
      // A confirmation must never record assistant playback. Existing playback
      // is stopped before microphone access; live caller audio is isolated by
      // the owning UI before it invokes this controller.
      stopReadAloud(true);
      void startDictation(true, request.capture).then((started) => {
        if (!started && approvalCapture) {
          const failed = approvalCapture;
          approvalCapture = null;
          failed.reject(new Error("Voice confirmation is unavailable."));
        }
      });
    });
  const stopDictation = () => {
    if (state.dictation === "confirm") void transcribe(recordGeneration);
    else if (state.dictation === "recording") stopCapture(true);
  };
  const cancelApprovalCapture = () => {
    if (approvalCapture) cancelDictation();
  };
  const stopReadAloud = (clearCache = false) => {
    readGeneration++;
    readAbort?.abort();
    readAbort = null;
    player?.stop();
    player = null;
    pendingRead = null;
    readSource = null;
    if (clearCache) cache = null;
    update({ readAloud: "idle", messageId: undefined, readError: undefined });
  };
  const read = (turn: SpeechTurn): Promise<void> => {
    if (
      !deps.canRead(turn) ||
      dictationBusy(state) ||
      speechTextReason(turn.text, turn.isFinal) ||
      !turn.messageEnvelope
    )
      return Promise.resolve();
    if (
      !deps.player &&
      (typeof globalThis.Audio !== "function" || typeof URL.createObjectURL !== "function")
    ) {
      update({
        readAloud: "error",
        messageId: turn.id,
        readError: "This browser cannot play Read aloud audio.",
      });
      return Promise.resolve();
    }
    const source = JSON.stringify([
      deps.actorId,
      turn.conversationId,
      turn.id,
      turn.text,
      turn.messageEnvelope,
    ]);
    if (state.readAloud === "loading" && readSource === source && pendingRead) return pendingRead;
    if (state.readAloud === "playing" && readSource === source) return Promise.resolve();
    const replayBlocked = isBlockedReplay(state, turn, source, cache);
    stopReadAloud();
    readSource = source;
    const generation = readGeneration;
    readAbort = new AbortController();
    const signal = readAbort.signal;
    update({ readAloud: "loading", messageId: turn.id, readError: undefined });
    const current = () => generation === readGeneration && !signal.aborted && deps.canRead(turn);
    const key = (value: Authorization) =>
      JSON.stringify([
        deps.actorId,
        value.conversationId,
        value.textHash,
        value.voice,
        value.model,
      ]);
    /** Refresh the authorization and cache for a fresh read; false means abandoned. */
    const ensureAuthorizedCache = async (): Promise<boolean> => {
      const auth = authorization(
        await deps.invoke(
          { action: "authorizeReplay", text: turn.text, envelope: turn.messageEnvelope },
          signal,
        ),
      );
      if (!current()) return false;
      if (
        cache &&
        cache.source === source &&
        cache.key === key(auth) &&
        cache.expiresAt > Date.now()
      )
        return true;
      cache = null;
      const result = (await deps.invoke(
        {
          action: "readAloud",
          text: turn.text,
          envelope: turn.messageEnvelope,
          requestId: crypto.randomUUID(),
        },
        signal,
      )) as { audio?: unknown; mime?: unknown };
      if (!current()) return false;
      const actual = authorization(result);
      if (actual.conversationId !== auth.conversationId || actual.textHash !== auth.textHash)
        throw new Error("Speech identity changed");
      if (typeof result.audio !== "string" || result.mime !== "audio/mpeg")
        throw new Error("Invalid speech audio");
      cache = {
        key: key(actual),
        source,
        expiresAt: actual.expiresAt,
        bytes: base64ToBytes(result.audio, MAX_SPEECH_AUDIO_BYTES),
      };
      return true;
    };
    pendingRead = (async () => {
      // A blocked replay already holds its unexpired authorization, so it reaches
      // `play()` with no captured callback and keeps the click gesture.
      let authorizationPassed = replayBlocked;
      try {
        if (!replayBlocked) {
          if (!(await ensureAuthorizedCache())) return;
          authorizationPassed = true;
        }
        if (!current() || !cache) return;
        let playbackFinished = false;
        player = (deps.player ?? browserPlayer)(
          cache.bytes,
          () => {
            playbackFinished = true;
            if (current()) {
              player = null;
              update({ readAloud: "completed" });
            }
          },
          () => {
            playbackFinished = true;
            if (current()) {
              player = null;
              update({
                readAloud: "error",
                readError: "Playback failed. Retry Read aloud explicitly.",
              });
            }
          },
        );
        const owned = player;
        await owned.play();
        if (!current()) {
          owned.stop();
          return;
        }
        if (!playbackFinished) update({ readAloud: "playing" });
      } catch (error) {
        if (!authorizationPassed) cache = null;
        if (current()) {
          player?.stop();
          player = null;
          update({
            readAloud:
              error instanceof DOMException && error.name === "NotAllowedError"
                ? "blocked"
                : "error",
            readError:
              error instanceof DOMException && error.name === "NotAllowedError"
                ? "Your browser paused playback. Select Play to hear the generated audio."
                : "Read aloud failed. Your answer and draft are unchanged. Retry explicitly.",
          });
        }
      } finally {
        if (generation === readGeneration) pendingRead = null;
      }
    })();
    return pendingRead;
  };
  return {
    updateCallbacks: (callbacks: SpeechCallbacks) => {
      deps.canDictate = callbacks.canDictate;
      deps.canCaptureApproval = callbacks.canCaptureApproval;
      deps.canRead = callbacks.canRead;
      deps.appendDraft = callbacks.appendDraft;
      deps.changed = callbacks.changed;
    },
    startDictation,
    startApprovalCapture,
    cancelApprovalCapture,
    stopDictation,
    cancelDictation,
    read,
    stopReadAloud,
    state: () => state,
    dispose: () => {
      cancelDictation();
      stopReadAloud(true);
    },
  };
}
